import React from 'react'
import { Logger } from 'wdc-cube'

import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { formatCurrency } from 'src/utils/formatter-utils'
import { bannerSx, classToFComponent, FCClassContext } from 'src/utils/views'

import { PriceTierEditorScope } from '../bl_scopes'
import type { PriceTierField } from '../bl_types'

const LOG = Logger.get('BL-PRICE-TIER-EDITOR')

const TierRow = styled('div')({
    display: 'grid',
    gridTemplateColumns: '120px 140px 160px 40px',
    gap: 10,
    alignItems: 'center'
})

const HintText = styled(Typography)({
    fontSize: 12,
    color: '#5f5f5f'
})

export type PriceTierEditorProps = {
    className?: string
    scope: PriceTierEditorScope
}

class PriceTierEditorClass implements FCClassContext<PriceTierEditorProps> {
    scope!: PriceTierEditorScope

    readonly emitTierAdded = () => this.scope.onTierAdded().catch(LOG.caught)

    readonly emitTierRemoved = (index: number) => () => this.scope.onTierRemoved(index).catch(LOG.caught)

    readonly emitTierChanged =
        (index: number, field: PriceTierField) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            this.scope.onTierChanged(index, field, event.target.value).catch(LOG.caught)

    onSyncState({ scope }: PriceTierEditorProps) {
        this.scope = scope
    }

    render({ className, scope }: PriceTierEditorProps) {
        this.scope = scope

        return (
            <Stack className={className} spacing={1.5}>
                <HintText>
                    O excedente total do mês cai em uma única faixa e é multiplicado pelo preço unitário dela. Exemplo:
                    120 registros excedentes na faixa &quot;101 ou mais&quot; a {formatCurrency(0.5)} resultam em{' '}
                    {formatCurrency(60)}.
                </HintText>

                <TierRow>
                    <Typography variant="caption">De (registros)</Typography>
                    <Typography variant="caption">Até (vazio = sem teto)</Typography>
                    <Typography variant="caption">Preço unitário (R$)</Typography>
                    <span />
                </TierRow>

                {scope.tiers.map((tier, index) => (
                    <TierRow key={`tier-${index}`}>
                        <TextField
                            type="number"
                            size="small"
                            value={Number.isFinite(tier.minQty) ? tier.minQty : ''}
                            disabled={scope.readOnly}
                            onChange={this.emitTierChanged(index, 'minQty')}
                            slotProps={{
                                htmlInput: { min: 1, step: 1 }
                            }}
                        />
                        <TextField
                            type="number"
                            size="small"
                            placeholder="sem teto"
                            value={tier.maxQty ?? ''}
                            disabled={scope.readOnly}
                            onChange={this.emitTierChanged(index, 'maxQty')}
                            slotProps={{
                                htmlInput: { min: 1, step: 1 }
                            }}
                        />
                        <TextField
                            type="number"
                            size="small"
                            value={Number.isFinite(tier.unitPrice) ? tier.unitPrice : ''}
                            disabled={scope.readOnly}
                            onChange={this.emitTierChanged(index, 'unitPrice')}
                            slotProps={{
                                htmlInput: { min: 0, step: 0.01 }
                            }}
                        />
                        {scope.readOnly ? (
                            <span />
                        ) : (
                            <Tooltip title="Remover faixa">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={this.emitTierRemoved(index)}
                                        disabled={!scope.canRemoveTier}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </TierRow>
                ))}

                {!scope.readOnly && (
                    <div>
                        <Button size="small" startIcon={<AddIcon />} onClick={this.emitTierAdded}>
                            Adicionar faixa
                        </Button>
                    </div>
                )}

                {scope.errors.length > 0 && (
                    <Alert
                        variant="outlined"
                        icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                        sx={bannerSx('warning')}
                    >
                        {scope.errors.map((error, index) => (
                            <div key={`err-${index}`}>{error}</div>
                        ))}
                    </Alert>
                )}
            </Stack>
        )
    }
}

export const PriceTierEditor = classToFComponent(PriceTierEditorClass, React)
export default PriceTierEditor
